import { useState, useEffect } from "react";
import { API_URL } from "../config/API_URLS.tsx";
import { authenticatedFetch } from "../auth/RequestSigner.tsx";
import { useNavigate } from 'react-router-dom';

import {
  Typography,
  Chip,
  Avatar,
} from "@material-tailwind/react";

const TABS = [
  {
    label: "Todos",
    value: "all", 
  },
  {
    label: "Pendientes",
    value: 4,
  },
  {
    label: "Aceptados",
    value: 5,
  },
  {
    label: "Rechazados",
    value: 6,
  },
];

export function AprendizRegistrationRequest() {
  const [selectedTab, setSelectedTab] = useState(4);
  const [aprendizData, setAprendizData] = useState([]);
  const [filteredRows, setFilteredRows] = useState({ items: [] });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Función para cargar datos de aprendices
  const fetchAprendizData = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(`${API_URL}/v2/aprendiz-status?page=${page}`, {
        method: 'get',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar los datos de solicitudes');
      }

      const data = await response.json();
      setAprendizData(data.items || []);
      setTotalItems(data.total_items || 0);

      // Aplicar filtros iniciales
      applyFilters(data.items || [], selectedTab, searchTerm);
    } catch (error) {
      console.error('Error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros a los datos
  const applyFilters = (data, tabValue, search) => {
    if (!data) return;

    const filtered = data.filter((row) => {
      // Filtrar por término de búsqueda (nombre o documento)
      // Asegurarse de convertir los valores a string antes de usar includes
      const matchesSearch = 
        row.name.toLowerCase().includes(search.toLowerCase()) || 
        (row.document && row.document.toString().includes(search)) ||
        (row.last_name && row.last_name.toLowerCase().includes(search.toLowerCase())) ||
        (row.email && row.email.toLowerCase().includes(search.toLowerCase()));

      // Filtrar por tab seleccionado
      const matchesTab = tabValue === 'all' ? true : row.status_id === tabValue;

      return matchesSearch && matchesTab;
    });

    setFilteredRows({ items: filtered });
  };

  // Cambiar de página
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Manejar cambio de pestaña
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    applyFilters(aprendizData, newValue, searchTerm);
  };

  // Manejar cambio en la búsqueda
  const handleSearchChange = (event) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    applyFilters(aprendizData, selectedTab, newSearchTerm);
  };

  // Cargar datos al iniciar o cambiar de página

  useEffect(() => {
    fetchAprendizData();
 // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Etiqueta de estado según state_id
  const getStatusLabel = (stateId) => {
    switch(stateId) {
      case 4:
        return "Pendiente";
      case 5:
        return "Aceptado";
      case 6:
        return "Rechazado";
      default:
        return "Creado";
    }
  };

  // Color de estado según state_id
  const getStatusColor = (stateId) => {
    switch(stateId) {
      case 4:
        return "orange";
      case 5:
        return "green";
      case 6:
        return "red";
      default:
        return "blue";
    }
  };

  // Función para el estilo de los botones de filtro
  const getFilterButtonStyles = (filterType) => {
    const baseStyles = "px-4 py-2 text-sm font-medium rounded-lg shadow-sm focus:outline-none transition-colors duration-200";

    if (selectedTab === filterType) {
      return `${baseStyles} bg-amber-600 text-white`;
    }
    return `${baseStyles} bg-gray-100 text-gray-700 hover:bg-gray-200`;
  };

  // Función para ver los detalles del aprendiz
  const handleViewAprendiz = (aprendiz) => {
    navigate(`/aprendiz-details/${aprendiz.document}`, {
      state: { aprendizData: aprendiz }
    });
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-testid="aprendiz-request-container">
      {/* Filtros de tipo de solicitud */}
      <div className="flex flex-wrap justify-center sm:justify-start gap-2 my-4" data-testid="request-type-filters">
        {TABS.map(({ label, value }) => (
          <button
            key={value}
            onClick={(event) => handleTabChange(event, value)}
            className={getFilterButtonStyles(value)}
            data-testid={`filter-${value}-button`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center p-4" data-testid="aprendiz-request-loading">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-700"></div>
          <span className="ml-2">Cargando solicitudes...</span>
        </div>
      )}

      {!loading && (
        <>
          <div className="mt-6 mb-4" data-testid="aprendiz-request-search-container">
            <div className="flex">
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Buscar por nombre o documento..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-colors duration-200"
                    data-testid="aprendiz-request-search-input"
                  />
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      applyFilters(aprendizData, selectedTab, '');
                    }}
                    className={`absolute right-2 top-1/2 transform -translate-y-1/2 text-purple-500 hover:text-purple-500 ${!searchTerm && 'hidden'}`}
                    data-testid="aprendiz-request-search-clear-button"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto shadow-md rounded-lg mt-6" data-testid="aprendiz-request-wrapper">
            <table className="min-w-full divide-y divide-gray-300 rounded-lg overflow-hidden" data-testid="aprendiz-request-table">
              <thead className="bg-gray-50" data-testid="aprendiz-request-table-head">
                <tr>
                  <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="aprendiz-request-header-name">Nombre</th>
                  <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="aprendiz-request-header-document">Documento</th>
                  <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell" data-testid="aprendiz-request-header-ficha">Ficha</th>
                  <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="aprendiz-request-header-status">Estado</th>
                  <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="aprendiz-request-header-actions">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-300" data-testid="aprendiz-request-table-body">         
                {filteredRows.items && filteredRows.items.length > 0 ? (
                  filteredRows.items.map(
                    (aprendiz, index) => (
                      <tr key={aprendiz.document} className="hover:bg-gray-50" data-testid={`aprendiz-request-row-${index}`}>
                        <td className="px-3 sm:px-4 py-3 whitespace-nowrap text-xs sm:text-sm text-gray-900" data-testid={`aprendiz-request-cell-name-${index}`}>
                          <div className="flex items-center gap-2">
                            <Avatar src={aprendiz.photo} alt={aprendiz.name} size="sm" data-testid={`aprendiz-request-avatar-${index}`} />
                            <div className="flex flex-col">
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {aprendiz.name} {aprendiz.last_name}
                              </Typography>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal opacity-70 text-xs"
                              >
                                {aprendiz.email}
                              </Typography>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 py-3 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-center" data-testid={`aprendiz-request-cell-document-${index}`}>
                          {aprendiz.document}
                        </td>
                        <td className="px-3 sm:px-4 py-3 whitespace-nowrap text-xs sm:text-sm text-gray-900 hidden md:table-cell" data-testid={`aprendiz-request-cell-ficha-${index}`}>
                          {aprendiz.ficha}
                        </td>
                        <td className="px-3 sm:px-4 py-3 whitespace-nowrap" data-testid={`aprendiz-request-cell-status-${index}`}>
                          <div className="flex justify-center">
                            <Chip
                              variant="ghost"
                              size="sm"
                              value={getStatusLabel(aprendiz.status_id)}
                              color={getStatusColor(aprendiz.status_id)}
                              className="py-1 px-2 text-xs min-w-0"
                            />
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 py-3 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-center" data-testid={`aprendiz-request-cell-actions-${index}`}>
                          <button
                            onClick={() => handleViewAprendiz(aprendiz)} 
                            className="text-amber-700 hover:text-amber-900 font-medium" 
                            data-testid={`aprendiz-request-view-link-${index}`}
                          >
                            Ver
                          </button>
                        </td>
                      </tr>
                    )
                  )
                ) : (
                  <tr data-testid="aprendiz-request-no-data">
                    <td colSpan={5} className="p-4 text-center">
                      No se encontraron datos con los criterios de búsqueda actuales.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredRows.items && filteredRows.items.length > 0 && (
            <div className="mt-4 mb-4 flex flex-col sm:flex-row items-center justify-center gap-4" data-testid="aprendiz-request-pagination">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="w-full sm:w-auto px-4 py-2 text-white text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="aprendiz-request-prev-button"
              >
                <div className={`absolute inset-0 ${page === 1 ? 'bg-gray-400' : 'bg-gradient-to-t from-amber-300 to-amber-700'} transition-transform duration-700 ease-in-out`}></div>
                <div className={`absolute inset-0 ${page === 1 ? 'bg-gray-400' : 'bg-gradient-to-b from-amber-300 to-amber-700'} opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out`}></div>
                <span className="z-10 text-gray-900">Anterior</span>
              </button>

              <span className="mx-2 py-1 text-sm text-center" data-testid="aprendiz-request-page-indicator">
                Página {page} de {Math.ceil(totalItems / 5)}
              </span>

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= Math.ceil(totalItems / 5)}
                className="w-full sm:w-auto px-4 py-2 text-white text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="aprendiz-request-next-button"
              >
                <div className={`absolute inset-0 ${page >= Math.ceil(totalItems / 5) ? 'bg-gray-400' : 'bg-gradient-to-t from-amber-300 to-amber-700'} transition-transform duration-700 ease-in-out`}></div>
                <div className={`absolute inset-0 ${page >= Math.ceil(totalItems / 5) ? 'bg-gray-400' : 'bg-gradient-to-b from-amber-300 to-amber-700'} opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out`}></div>
                <span className="z-10 text-gray-900">Siguiente</span>
              </button>
            </div>
          )}
        </>
      )}

      {error && (
        <div className="text-center text-red-600 p-4" data-testid="aprendiz-request-error">
          Error al cargar las solicitudes: {error}
        </div>
      )}
    </div>
  );
}
